import { useLayoutEffect, useSyncExternalStore } from 'react';

/**
 * Reasons the opening splash should stay up past auth, keyed so several surfaces can hold it independently.
 * The shell holds it until the flags and the funding phase that pick a shell have arrived: drawing one shell
 * and swapping to another is what read as the page loading twice.
 */
const holds = new Set<string>();
const listeners = new Set<() => void>();
let openingSequenceEnded = false;

function holdSplash(key: string, held: boolean): void {
  if (openingSequenceEnded) return;
  if (holds.has(key) === held) return;
  if (held) {
    holds.add(key);
  } else {
    holds.delete(key);
  }
  listeners.forEach((listener) => listener());
}

/**
 * Closes the opening sequence for the rest of the session: once the logo has begun to dissolve, a surface
 * that starts loading can no longer bring it back. Navigating to a page that waits on its own data shows
 * that page's skeleton, never the logo again.
 */
export function endSplashOpeningSequence(): void {
  if (openingSequenceEnded) return;
  openingSequenceEnded = true;
  if (holds.size === 0) return;
  holds.clear();
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return holds.size > 0;
}

function getServerSnapshot(): boolean {
  return false;
}

export function useSplashHeld(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Holds the splash while `held`, releasing on unmount. Layout-timed so the hold lands before the first paint. */
export function useSplashHold(key: string, held: boolean): void {
  useLayoutEffect(() => {
    holdSplash(key, held);
    return () => holdSplash(key, false);
  }, [key, held]);
}
